import { useRef, useState } from 'react';
import { useI18n } from '@/shared/i18n/useT';
import { normalizeText } from '@/shared/components/Grid/textNormalize';
import { Button } from '@/shared/components/ui/Button';
import { ColorPicker } from '@/shared/components/ui/ColorPicker';
import type { FuenteChips, ItemChip } from '@/shared/components/ui/ChipSelect';

export interface ChipListEditorProps {
  /** Lista vigente. El editor la actualiza de forma optimista vía `onItemsChange`. */
  items: readonly ItemChip[];
  fuente: FuenteChips;
  /** Encabezado del panel; se omite el `<h4>` si no se pasa. */
  title?: string;
  /** Valor seleccionado, para seguirlo cuando se renombra o se elimina. */
  value?: string;
  onChange?: (value: string) => void;
  onItemsChange: (items: ItemChip[]) => void;
  /** Releer desde la fuente tras un guardado fallido o un alta. */
  onReload: () => void;
  onError: (message: string | null) => void;
  className?: string;
}

/**
 * Panel de edición de una lista de chips: reordenar arrastrando, color, texto,
 * baja y alta. Extraído de `ChipSelect` para que el mismo editor sirva tanto
 * dentro del desplegable como fuera (la grilla lo abre desde la barra de
 * formato). Las capacidades salen de la fuente: sin `reordenar` no hay manija,
 * sin `eliminar` no hay tacho, sin `agregar` no hay fila de alta.
 */
export function ChipListEditor({
  items,
  fuente,
  title,
  value,
  onChange,
  onItemsChange,
  onReload,
  onError,
  className = '',
}: ChipListEditorProps) {
  const { t } = useI18n();
  const [nuevoValor, setNuevoValor] = useState('');
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  const arrastrandoRef = useRef<string | null>(null);

  const guardarColor = async (item: ItemChip, color: string | null) => {
    onItemsChange(items.map((x) => (x.clave === item.clave ? { ...x, color } : x)));
    const errorMsg = await fuente.guardarColor(item.clave, color);
    if (errorMsg) {
      onError(errorMsg);
      onReload();
    }
  };

  const cambiarTexto = (item: ItemChip, texto: string) => {
    onItemsChange(items.map((x) => (x.clave === item.clave ? { ...x, texto } : x)));
  };

  const guardarTexto = async (item: ItemChip) => {
    if (!fuente.guardarTexto) return;
    const texto = normalizeText(item.texto);
    if (!texto || texto === item.clave) {
      onReload();
      return;
    }
    const resultado = await fuente.guardarTexto(item.clave, texto);
    if (resultado.error) {
      onError(resultado.error);
      onReload();
      return;
    }
    if (value === item.clave) onChange?.(resultado.nuevaClave);
    onReload();
  };

  const eliminarValor = async (item: ItemChip) => {
    if (!fuente.eliminar) return;
    const errorMsg = await fuente.eliminar(item.clave);
    if (errorMsg) {
      onError(errorMsg);
      return;
    }
    if (value === item.clave) onChange?.('');
    onReload();
  };

  const soltar = async (claveDestino: string) => {
    const claveOrigen = arrastrandoRef.current;
    arrastrandoRef.current = null;
    setArrastrando(null);
    if (!fuente.reordenar || !claveOrigen || claveOrigen === claveDestino) return;
    const desde = items.findIndex((i) => i.clave === claveOrigen);
    const hasta = items.findIndex((i) => i.clave === claveDestino);
    if (desde === -1 || hasta === -1) return;
    const reordenados = [...items];
    const movido = reordenados[desde];
    if (!movido) return;
    reordenados.splice(desde, 1);
    reordenados.splice(hasta, 0, movido);
    onItemsChange(reordenados);
    const errorMsg = await fuente.reordenar(reordenados.map((i) => i.clave));
    if (errorMsg) {
      onError(errorMsg);
      onReload();
    }
  };

  /** A→Z; si ya está ascendente, Z→A. */
  const ordenar = async () => {
    if (!fuente.reordenar) return;
    const cmp = (a: ItemChip, b: ItemChip) =>
      a.texto.localeCompare(b.texto, undefined, { sensitivity: 'base', numeric: true });
    const asc = [...items].sort(cmp);
    const yaAsc = asc.every((x, i) => x.clave === items[i]?.clave);
    const ordenados = yaAsc ? asc.reverse() : asc;
    onItemsChange(ordenados);
    const errorMsg = await fuente.reordenar(ordenados.map((i) => i.clave));
    if (errorMsg) {
      onError(errorMsg);
      onReload();
    }
  };

  const agregarValor = async () => {
    if (!fuente.agregar) return;
    const texto = normalizeText(nuevoValor);
    if (!texto) return;
    const errorMsg = await fuente.agregar(texto);
    if (errorMsg) {
      onError(errorMsg);
      return;
    }
    setNuevoValor('');
    onReload();
  };

  return (
    <div className={`font-normal ${className}`}>
      {title && <h4 className="mb-2 text-xs font-bold uppercase text-muted">{title}</h4>}
      <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
        {items.map((item) => (
          <div
            key={item.clave}
            onDragOver={(e) => fuente.reordenar && e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void soltar(item.clave);
            }}
            className={`flex items-center gap-2 rounded-lg ${arrastrando === item.clave ? 'opacity-40' : ''}`}
          >
            {fuente.reordenar && (
              <span
                draggable
                onDragStart={(e) => {
                  arrastrandoRef.current = item.clave;
                  setArrastrando(item.clave);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={() => {
                  arrastrandoRef.current = null;
                  setArrastrando(null);
                }}
                title={t('chipSelect.dragToReorder')}
                className="flex h-6 w-4 shrink-0 cursor-grab items-center justify-center text-muted active:cursor-grabbing"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                  <circle cx="9" cy="6" r="1.6" />
                  <circle cx="15" cy="6" r="1.6" />
                  <circle cx="9" cy="12" r="1.6" />
                  <circle cx="15" cy="12" r="1.6" />
                  <circle cx="9" cy="18" r="1.6" />
                  <circle cx="15" cy="18" r="1.6" />
                </svg>
              </span>
            )}
            <ColorPicker
              valor={item.color}
              onChange={(color) => void guardarColor(item, color)}
              coloresUsados={items.filter((x) => x.clave !== item.clave && x.color).map((x) => x.color as string)}
              trigger={
                <span
                  className="block h-6 w-6 shrink-0 rounded-full border border-border cursor-pointer transition-transform hover:scale-105"
                  style={{
                    background: item.color ?? 'transparent',
                    borderStyle: item.color ? 'solid' : 'dashed',
                  }}
                />
              }
            />
            <input
              value={item.texto}
              onChange={(e) => cambiarTexto(item, e.target.value)}
              onBlur={() => void guardarTexto(item)}
              readOnly={!fuente.guardarTexto}
              className="h-8 flex-1 rounded-lg border border-border bg-surface px-2 text-sm text-text outline-none focus:border-accent disabled:opacity-60"
            />
            {fuente.eliminar && (
              <button
                type="button"
                title={t('chipSelect.delete')}
                onClick={() => void eliminarValor(item)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-danger/10 hover:text-danger"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
      {fuente.agregar && (
        <div className="mt-2 flex items-center gap-2">
          <input
            value={nuevoValor}
            onChange={(e) => setNuevoValor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void agregarValor();
              }
            }}
            placeholder={t('chipSelect.newValuePlaceholder')}
            className="h-8 flex-1 rounded-lg border border-border bg-surface px-2 text-sm text-text outline-none focus:border-accent"
          />
          <Button variant="outline" size="sm" onClick={() => void agregarValor()}>
            +
          </Button>
          {fuente.reordenar && items.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              title={t('chipSelect.sortAlphabetically')}
              aria-label={t('chipSelect.sortAlphabetically')}
              onClick={() => void ordenar()}
            >
              A↓Z
            </Button>
          )}
        </div>
      )}
      {!fuente.agregar && fuente.reordenar && items.length > 1 && (
        <div className="mt-2 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            title={t('chipSelect.sortAlphabetically')}
            aria-label={t('chipSelect.sortAlphabetically')}
            onClick={() => void ordenar()}
          >
            A↓Z
          </Button>
        </div>
      )}
    </div>
  );
}

export default ChipListEditor;
